import React, { useState } from 'react';
import { reviewsAPI } from '../../utils/api';
import RatingStars from './RatingStars';
import { toast } from 'sonner';
import { Camera, Video, X, Shield } from 'lucide-react';

const ReviewForm = ({ productId, onReviewSubmitted }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [verifyMode, setVerifyMode] = useState(false);
  const [verified, setVerified] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', title: '', text: '', orderId: '',
  });

  const handleVerifyPurchase = async () => {
    if (!form.email || !form.orderId) {
      toast.error('Please enter email and order ID');
      return;
    }
    try {
      const { data } = await reviewsAPI.verifyPurchase(productId, form.email, form.orderId);
      if (data.verified) {
        setVerified(true);
        toast.success('Purchase verified!');
      } else {
        toast.error('Could not verify purchase. Check your email and order ID.');
      }
    } catch (_) {
      toast.error('Verification failed');
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) { toast.error('Maximum 5 photos allowed'); return; }
    setUploading(true);
    try {
      const results = await Promise.all(files.map(f => reviewsAPI.uploadMedia(f)));
      setPhotos(prev => [...prev, ...results.map(r => r.data.path)]);
    } catch (_) { toast.error('Photo upload failed'); }
    finally { setUploading(false); }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error('Video must be under 50MB'); return; }
    if (videos.length >= 1) { toast.error('Maximum 1 video allowed'); return; }
    setUploading(true);
    try {
      const { data } = await reviewsAPI.uploadMedia(file);
      setVideos([data.path]);
    } catch (_) { toast.error('Video upload failed'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) { toast.error('Please select a rating'); return; }
    if (!form.name || !form.email || !form.text) { toast.error('Please fill name, email, and review'); return; }
    setSubmitting(true);
    try {
      await reviewsAPI.create({
        product_id: productId,
        customer_name: form.name,
        customer_email: form.email,
        rating,
        title: form.title || null,
        text: form.text,
        photos,
        videos,
        order_id: verified ? form.orderId : null,
      });
      toast.success('Thank you for your review!');
      setOpen(false);
      setRating(0);
      setForm({ name: '', email: '', title: '', text: '', orderId: '' });
      setPhotos([]);
      setVideos([]);
      setVerified(false);
      onReviewSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally { setSubmitting(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="bg-[#1A1A18] text-white hover:bg-[#C29B57] transition-colors px-6 py-3 font-outfit uppercase tracking-wider text-sm"
        data-testid="write-review-button"
      >
        Write a Review
      </button>
    );
  }

  return (
    <div className="bg-white border border-[#EBE4D5] rounded-lg p-6" data-testid="review-form">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-cormorant text-2xl">Write a Review</h3>
        <button onClick={() => setOpen(false)} className="text-[#8C8577] hover:text-[#1A1A18]"><X className="w-5 h-5" /></button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Star Rating */}
        <div>
          <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">Your Rating *</label>
          <RatingStars rating={rating} size="lg" interactive onRate={setRating} />
        </div>

        {/* Verified Purchase */}
        <div className="border border-[#EBE4D5] rounded-md p-4">
          {verified ? (
            <div className="flex items-center gap-2 text-[#3A5A40]" data-testid="verified-badge">
              <Shield className="w-4 h-4" />
              <span className="font-outfit text-sm font-medium">Verified Purchase</span>
            </div>
          ) : (
            <>
              <button type="button" onClick={() => setVerifyMode(!verifyMode)} className="flex items-center gap-2 font-outfit text-sm text-[#C29B57] hover:underline">
                <Shield className="w-4 h-4" />
                {verifyMode ? 'Hide verification' : 'Verify your purchase (optional)'}
              </button>
              {verifyMode && (
                <div className="mt-3 space-y-3">
                  <input type="email" placeholder="Your order email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#EBE4D5] rounded-md px-4 py-2 font-outfit text-sm outline-none focus:border-[#C29B57]" data-testid="verify-email" />
                  <input type="text" placeholder="Order ID" value={form.orderId} onChange={(e) => setForm({ ...form, orderId: e.target.value })} className="w-full border border-[#EBE4D5] rounded-md px-4 py-2 font-outfit text-sm outline-none focus:border-[#C29B57]" data-testid="verify-order-id" />
                  <button type="button" onClick={handleVerifyPurchase} className="bg-[#C29B57] text-white px-4 py-2 rounded-md font-outfit text-sm hover:bg-[#99733D] transition-colors" data-testid="verify-button">Verify Purchase</button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Name & Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">Name *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full border border-[#EBE4D5] rounded-md px-4 py-2.5 font-outfit text-sm outline-none focus:border-[#C29B57]" data-testid="review-name" />
          </div>
          <div>
            <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">Email *</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full border border-[#EBE4D5] rounded-md px-4 py-2.5 font-outfit text-sm outline-none focus:border-[#C29B57]" data-testid="review-email" />
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">Review Title</label>
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Sum up your experience" className="w-full border border-[#EBE4D5] rounded-md px-4 py-2.5 font-outfit text-sm outline-none focus:border-[#C29B57]" data-testid="review-title" />
        </div>

        {/* Review Text */}
        <div>
          <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">Your Review *</label>
          <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required rows="4" placeholder="Tell others about your experience..." className="w-full border border-[#EBE4D5] rounded-md px-4 py-2.5 font-outfit text-sm outline-none focus:border-[#C29B57] resize-none" data-testid="review-text" />
        </div>

        {/* Photo/Video Upload */}
        <div className="flex gap-4 flex-wrap">
          <label className="flex items-center gap-2 px-4 py-2 border border-[#EBE4D5] rounded-md cursor-pointer hover:border-[#C29B57] transition-colors">
            <Camera className="w-4 h-4 text-[#8C8577]" />
            <span className="font-outfit text-sm text-[#8C8577]">Add Photos ({photos.length}/5)</span>
            <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload" />
          </label>
          <label className="flex items-center gap-2 px-4 py-2 border border-[#EBE4D5] rounded-md cursor-pointer hover:border-[#C29B57] transition-colors">
            <Video className="w-4 h-4 text-[#8C8577]" />
            <span className="font-outfit text-sm text-[#8C8577]">Add Video ({videos.length}/1)</span>
            <input type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" data-testid="video-upload" />
          </label>
          {uploading && <span className="font-outfit text-sm text-[#C29B57]">Uploading...</span>}
        </div>

        {/* Preview uploaded media */}
        {(photos.length > 0 || videos.length > 0) && (
          <div className="flex gap-2 flex-wrap">
            {photos.map((p, i) => (
              <div key={p} className="relative w-16 h-16 rounded overflow-hidden">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-0 right-0 bg-[#9E2A2B] text-white text-xs px-1">x</button>
              </div>
            ))}
            {videos.map((v) => (
              <div key={v} className="relative w-16 h-16 rounded overflow-hidden bg-[#1A1A18] flex items-center justify-center">
                <Video className="w-6 h-6 text-white" />
                <button type="button" onClick={() => setVideos([])} className="absolute top-0 right-0 bg-[#9E2A2B] text-white text-xs px-1">x</button>
              </div>
            ))}
          </div>
        )}

        <button type="submit" disabled={submitting} className="w-full bg-[#1A1A18] text-white hover:bg-[#C29B57] transition-colors py-3 font-outfit uppercase tracking-wider text-sm disabled:opacity-50" data-testid="submit-review-button">
          {submitting ? 'Submitting...' : 'Submit Review'}
        </button>
      </form>
    </div>
  );
};

export default ReviewForm;
